// FTUE-v2 reveal entry (capability `boat-reveal`). The reveal is now PUBLIC —
// no RequireAuth gate. Which experience renders depends on what the URL carries:
//
//   ?boatId=…   → the AUTHENTICATED post-claim reveal (the existing experience:
//                 devices, alarms, live). Reached after a claim, or by an owner.
//   #rt=…        → the ANONYMOUS, account-free pre-claim reveal: name beat →
//                 "we found your gateways" → create-account-or-login → claim.
//   (stash)      → nothing in the URL but a sessionStorage resume stash from
//                 "Continue with Google" (lib/revealResume.ts): re-enter the
//                 anonymous reveal at the account step with the stashed token
//                 + name; it auto-claims once the session probe lands.
//   neither      → a prompt to open the reveal from the boat.
//
// The reveal token is captured ONCE (before <AnonymousReveal> strips it from the
// address bar) so a later re-render can't lose it.

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { RequireAuth } from '../components/RequireAuth';
import { loadRevealResume } from '../lib/revealResume';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { AnonymousReveal } from './AnonymousReveal';
import { Reveal } from './Reveal';

function readRevealToken(): string | null {
  const hash = window.location.hash;
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const rt = params.get('rt');
  return rt !== null && rt.length > 0 ? rt : null;
}

export function RevealEntry() {
  const [searchParams] = useSearchParams();
  const boatId = (searchParams.get('boatId') ?? '').trim();
  // Snapshot the fragment token at first render — AnonymousReveal scrubs the
  // hash on mount, so reading it lazily later would come back empty.
  const [revealToken] = useState<string | null>(() => readRevealToken());
  // Same snapshot treatment for the Google-return stash so re-renders can't
  // lose it. A live fragment token always wins over a stash.
  const [resume] = useState(() => loadRevealResume());

  // Post-claim / owner view still requires auth (preserves the old gate); only
  // the anonymous pre-claim branch is open.
  if (boatId !== '')
    return (
      <RequireAuth>
        <Reveal />
      </RequireAuth>
    );
  if (revealToken !== null) return <AnonymousReveal revealToken={revealToken} />;
  if (resume !== null)
    return <AnonymousReveal revealToken={resume.revealToken} initialName={resume.name} resume />;
  return <RevealEmpty />;
}

function RevealEmpty() {
  useDocumentTitle('Your boat - Neomaris');
  return (
    <main className="auth-panel">
      <div className="tb-card reveal-empty">
        <div className="tb-empty">
          <span className="tb-empty__icon" aria-hidden="true">
            <BoatIcon />
          </span>
          <h2 className="tb-title-2">Open this from your boat</h2>
          <p className="tb-subhead">
            To set up a new boat, open your boat&apos;s local page and follow the &ldquo;open your
            boat in the cloud&rdquo; link.
          </p>
          <p className="tb-subhead">Already have an account? Head to your dashboard.</p>
          <Link className="tb-btn tb-btn--tinted tb-btn--md reveal-empty__action" to="/dashboard">
            Open your dashboard
          </Link>
        </div>
      </div>
    </main>
  );
}

// Lucide sailboat.
function BoatIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="26"
      height="26"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M22 18H2a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4Z" />
      <path d="M21 14 10 2 3 14h18Z" />
      <path d="M10 2v16" />
    </svg>
  );
}
